import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { PageHeader, StatusBadge, LoadingSkeleton, EmptyState } from "@/components/ui-system";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  User, Package, Receipt, Clock, AlertTriangle, MapPin, RotateCcw, RefreshCw,
  Warehouse, MessageSquare, Building2, TrendingUp, ArrowRight,
} from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";

const REASON_LABELS = {
  damaged: "Damaged / defective product", wrong_item: "Wrong item delivered",
  not_as_described: "Product not as described", size_fit: "Size / fit issue",
  quality: "Quality not as expected", missing_parts: "Missing parts / accessories",
  no_longer_needed: "No longer needed", other: "Other",
};

const inr = (n) => (n == null ? "-" : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

const Row = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <dt className="text-sm text-slate-500 shrink-0">{label}</dt>
    <dd className={`text-sm text-slate-800 text-right break-words ${mono ? "font-mono" : ""}`}>{value ?? "-"}</dd>
  </div>
);

const B2CAdminReturnDetail = ({ user, onLogout }) => {
  const { returnId } = useParams();
  const navigate = useNavigate();

  const [ret, setRet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReturn = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/b2c/admin/returns/${returnId}`);
      setRet(data);
    } catch (e) {
      setError(errDetail(e, "Could not load this return"));
    } finally {
      setLoading(false);
    }
  }, [returnId]);

  useEffect(() => { fetchReturn(); }, [fetchReturn]);

  if (loading) {
    return (
      <AdminLayout user={user} onLogout={onLogout}>
        <div className="space-y-6"><LoadingSkeleton variant="detail" /></div>
      </AdminLayout>
    );
  }

  if (error || !ret) {
    return (
      <AdminLayout user={user} onLogout={onLogout}>
        <div className="space-y-6">
          <PageHeader title="Return" backTo={() => navigate("/admin/b2c/returns")} />
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <EmptyState icon={AlertTriangle} title="Return not found"
                description={error || "This return doesn't exist."}
                action={<Button variant="outline" onClick={() => navigate("/admin/b2c/returns")}>Back to returns</Button>} />
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  const history = Array.isArray(ret.status_history) ? ret.status_history : [];
  const rate = ret.seller_rate;

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto space-y-6">
        <PageHeader
          title={ret.return_number}
          subtitle={`Created ${formatToIST(ret.created_at)}`}
          backTo={() => navigate("/admin/b2c/returns")}
          badge={
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={ret.status} kind="b2c" size="lg" />
              {ret.type === "exchange" ? (
                <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200"><RefreshCw className="w-3 h-3 mr-1" />Exchange</Badge>
              ) : (
                <Badge variant="outline" className="border-slate-200 text-slate-700"><RotateCcw className="w-3 h-3 mr-1" />Return</Badge>
              )}
              {(ret.seller_brand_name || ret.seller_id) && (
                <Badge variant="outline" className="border-slate-200 text-slate-700">
                  <Building2 className="w-3.5 h-3.5 mr-1 text-slate-400" />{ret.seller_brand_name || ret.seller_id}
                </Badge>
              )}
            </div>
          }
        />

        {/* Reverse AWB / courier strip */}
        {(ret.awb || ret.courier_code) && (
          <Card className="border-0 shadow-sm">
            <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 py-4">
              {ret.courier_code && (
                <div><p className="text-xs text-slate-500">Courier</p><p className="text-sm font-semibold text-slate-800">{ret.courier_name || ret.courier_code}</p></div>
              )}
              {ret.awb && (
                <div><p className="text-xs text-slate-500">Reverse AWB</p><p className="text-sm font-mono font-semibold text-slate-800">{ret.awb}</p></div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Admin economics */}
        <Card className="border-0 shadow-sm border-l-4 border-l-brand">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-brand" /> Economics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div><p className="text-xs text-slate-500">Aggregator</p><p className="text-sm font-semibold capitalize text-slate-800">{ret.aggregator || "-"}</p>{ret.aggregator_partner_id && <p className="text-[10px] font-mono text-slate-400 truncate" title={ret.aggregator_partner_id}>{ret.aggregator_partner_id}</p>}</div>
              <div><p className="text-xs text-slate-500">Our cost</p><p className="text-sm font-semibold tabular-nums text-slate-800">{inr(ret.cost_total)}</p></div>
              <div><p className="text-xs text-slate-500">Seller charge</p><p className="text-sm font-semibold tabular-nums text-slate-800">{inr(ret.seller_charge)}</p></div>
              <div><p className="text-xs text-slate-500">Margin</p><p className={`text-sm font-bold tabular-nums ${ret.margin == null ? "text-slate-400" : ret.margin >= 0 ? "text-green-700" : "text-red-600"}`}>{inr(ret.margin)}{ret.margin_pct != null ? ` (${ret.margin_pct}%)` : ""}</p></div>
            </div>
          </CardContent>
        </Card>

        {/* Exchange replacement link */}
        {ret.type === "exchange" && ret.replacement_order_id && (
          <Card className="border-0 shadow-sm border-l-4 border-l-indigo-300">
            <CardContent className="flex items-center justify-between gap-3 py-4">
              <div>
                <p className="text-xs text-slate-500">Replacement forward order</p>
                <p className="text-sm font-semibold text-slate-800">{ret.replacement_order_number}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate(`/admin/b2c/orders/${ret.replacement_order_id}`)}>
                Open order <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Clock className="w-5 h-5 text-brand" /> Status timeline</CardTitle></CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <p className="text-sm text-slate-500">No status updates yet.</p>
                ) : (
                  <ol className="relative border-l border-slate-200 ml-2 space-y-5">
                    {history.map((h, i) => (
                      <li key={i} className="ml-4">
                        <span className={`absolute -left-1.5 mt-1 h-3 w-3 rounded-full ${i === history.length - 1 ? "bg-brand" : "bg-slate-300"}`} />
                        <StatusBadge status={h.status} kind="b2c" size="sm" />
                        {h.at && <p className="mt-0.5 text-xs text-slate-400">{formatToIST(h.at)}</p>}
                      </li>
                    ))}
                  </ol>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><MessageSquare className="w-5 h-5 text-brand" /> Reason</CardTitle></CardHeader>
              <CardContent>
                <p className="text-sm font-medium text-slate-800">{REASON_LABELS[ret.reason] || ret.reason}</p>
                {ret.reason_note && <p className="mt-1 text-sm text-slate-600">{ret.reason_note}</p>}
                <div className="mt-3 text-xs text-slate-500">
                  Against order{" "}
                  <button className="text-brand hover:underline" onClick={() => navigate(`/admin/b2c/orders/${ret.order_id}`)}>{ret.order_number}</button>
                  {ret.original_awb ? ` · AWB ${ret.original_awb}` : ""}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Card className="border-0 shadow-sm">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><User className="w-5 h-5 text-brand" /> Pickup from (buyer)</CardTitle></CardHeader>
                <CardContent>
                  <dl>
                    <Row label="Name" value={ret.buyer?.name} />
                    <Row label="Phone" value={ret.buyer?.phone} />
                    <Row label="Address" value={
                      <span className="inline-flex items-start gap-1 justify-end">
                        <MapPin className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
                        <span>{ret.buyer?.address}{ret.buyer?.landmark ? `, ${ret.buyer.landmark}` : ""}{", "}{[ret.buyer?.city, ret.buyer?.state, ret.buyer?.pincode].filter(Boolean).join(", ")}</span>
                      </span>
                    } />
                  </dl>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Warehouse className="w-5 h-5 text-brand" /> Return to</CardTitle></CardHeader>
                <CardContent>
                  <dl>
                    <Row label="Destination" value="Seller pickup location" />
                    <Row label="Zone" value={ret.zone ? <Badge variant="outline" className="border-slate-200">{ret.zone}</Badge> : "-"} />
                    <Row label="Charged weight" value={<span className="font-semibold">{ret.package?.charged_weight_kg ?? "-"} kg</span>} />
                    <Row label="Dimensions" value={`${ret.package?.length_cm ?? "-"}×${ret.package?.breadth_cm ?? "-"}×${ret.package?.height_cm ?? "-"} cm`} />
                  </dl>
                </CardContent>
              </Card>
            </div>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Receipt className="w-5 h-5 text-brand" /> Returned items</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {(ret.products || []).map((p, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
                      <p className="text-xs text-slate-500">{p.sku ? `SKU ${p.sku} · ` : ""}Qty {p.qty} × {inr(p.unit_price)}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {rate && (
              <Card className="border-0 shadow-sm">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Package className="w-5 h-5 text-brand" /> Reverse-shipping charges</CardTitle></CardHeader>
                <CardContent>
                  <dl>
                    <Row label="Freight" value={inr(rate.freight)} />
                    <Row label={`GST${rate.breakdown?.gst_pct ? ` (${rate.breakdown.gst_pct}%)` : ""}`} value={inr(rate.gst)} />
                    <div className="flex items-center justify-between border-t pt-2 mt-1">
                      <dt className="text-sm font-semibold text-slate-700">Charged to seller</dt>
                      <dd className="text-base font-bold text-slate-800 tabular-nums">{inr(rate.total)}</dd>
                    </div>
                  </dl>
                  {ret.refunded_at && <p className="mt-2 text-xs text-green-700">Refunded on cancellation.</p>}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default B2CAdminReturnDetail;
